import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WelcomeSection } from "../WelcomeSection";
import { FormDescription } from "../FormDescription";
import { Check, Calendar as CalendarIcon, Clock, Loader2 } from "lucide-react";
import Calendar from './components/Calendar';
import TimeSlots from './components/TimeSlots';
import { Button } from './components/ui/button';
import ScheduleConfirmationDialog from './ScheduleConfirmationDialog';
import ScheduleCompletedStatus from './ScheduleCompletedStatus';
import { fetchAppointmentAvailability } from '@features/appointment/appointmentSlice';
import { appointmentService } from '@features/appointment/appointmentService';
import { AppDispatch, RootState } from '@app/store';
import { AppointmentAvailability, DayAvailability, TimeSlotAvailability } from '@features/appointment/types';
import { format, parseISO } from 'date-fns';
import { ExtendedFilledFormState } from '@features/filledForm/filledFormSlice';

interface ScheduleSuccessPageProps {
  activeStep?: number;
  totalSteps?: number;
  processId?: string;
  formTitle?: string;
  appointmentId?: string;
  filledFormId?: string | null;
}

export const ScheduleSuccessPage: React.FC<ScheduleSuccessPageProps> = ({
  formTitle = "Formulário enviado com sucesso!",
  processId,
  appointmentId,
  filledFormId
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [scheduleComplete, setScheduleComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schedulingInProgress, setSchedulingInProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availabilityData, setAvailabilityData] = useState<AppointmentAvailability | null>(null);
  const [scheduleResponse, setScheduleResponse] = useState<any>(null);
  
  // Obter o filledFormId do Redux
  const reduxFilledFormId = useSelector((state: RootState) => 
    (state.filledForm as ExtendedFilledFormState)?.currentFilledFormId
  );

  // Usar o filledFormId da prop ou do Redux, priorizando o Redux
  const actualFormId = useMemo(() => {
    // Primeiro, verificar se temos um ID válido do Redux
    if (reduxFilledFormId && reduxFilledFormId !== 'new') {
      console.log('Usando filledFormId do Redux:', reduxFilledFormId);
      return reduxFilledFormId;
    }
    
    // Se não, verificar se temos um ID válido das props
    if (filledFormId && filledFormId !== 'new') {
      console.log('Usando filledFormId das props:', filledFormId);
      return filledFormId;
    }
    
    // Como último recurso, usar o processId se não for 'new'
    if (processId && processId !== 'new') {
      console.log('Usando processId como fallback:', processId);
      return processId;
    }
    
    // Se chegamos aqui, não temos um ID válido
    console.error('ALERTA: Não foi possível encontrar um ID válido para o formulário preenchido');
    return null;
  }, [filledFormId, reduxFilledFormId, processId]);

  // Log para debug
  useEffect(() => {
    console.log('ScheduleSuccessPage - IDs disponíveis:', {
      processId,
      filledFormId,
      reduxFilledFormId,
      actualFormId,
      isNew: actualFormId === 'new'
    });
    
    // Se o ID for "new", isso é um problema e deve ser registrado
    if (actualFormId === 'new') {
      console.error('ALERTA: Usando "new" como ID do formulário preenchido. Isso pode causar erros na API.');
    }
  }, [processId, filledFormId, reduxFilledFormId, actualFormId]);

  // Get appointment availability from Redux store
  const availability = useSelector((state: RootState) => 
    state.appointment?.currentItem as AppointmentAvailability | null
  );
  
  // Get loading state from Redux store
  const isLoading = useSelector((state: RootState) => state.appointment?.loading);

  // Função de manipulação de data com useCallback para garantir estabilidade
  const handleDateChange = useCallback((date: Date | undefined) => {
    console.log('Data selecionada alterada:', date);
    setSelectedDate(date);
    // Resetar o horário selecionado quando a data mudar
    setSelectedTime(null);
  }, []);

  // Find the selected day's availability - usando formato yyyy-MM-dd para comparação
  const findDayAvailability = useCallback((date: Date | undefined, days: DayAvailability[] | undefined) => {
    if (!date || !days || days.length === 0) return undefined;
    
    // Formatar a data ignorando o fuso horário
    const formattedDate = format(date, 'yyyy-MM-dd');
    console.log('Buscando disponibilidade para:', formattedDate);
    
    // Log de todas as datas disponíveis para depuração
    console.log('Todas as datas disponíveis:', days.map(d => d.date));
    
    // Encontrar o dia correspondente
    const foundDay = days.find(day => day.date === formattedDate);
    console.log('Dia encontrado:', foundDay);
    
    return foundDay;
  }, []);

  // Calcular a disponibilidade do dia selecionado usando useCallback
  const selectedDayAvailability = useCallback(() => {
    if (!selectedDate || !availabilityData?.days) {
      return undefined;
    }
    
    // Formatar a data ignorando o fuso horário
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    
    // Encontrar o dia correspondente diretamente
    const foundDay = availabilityData.days.find(day => day.date === formattedDate);
    
    return foundDay;
  }, [selectedDate, availabilityData]);

  // Available time slots for the selected date
  const getAvailableTimeSlots = useCallback(() => {
    const dayAvailability = selectedDayAvailability();
    
    if (!dayAvailability) {
      console.log('Nenhuma disponibilidade encontrada para a data selecionada');
      return [];
    }
    
    const slots = dayAvailability.timeSlots
      .filter(slot => slot.available)
      .map(slot => slot.time);
    
    console.log('Horários disponíveis encontrados:', slots);
    return slots;
  }, [selectedDayAvailability]);

  // Log para debug sempre que a data selecionada mudar
  useEffect(() => {
    if (selectedDate) {
      console.log('==== ALTERAÇÃO DE DATA DETECTADA ====');
      console.log('Data selecionada:', selectedDate);
      console.log('Data selecionada ISO:', selectedDate.toISOString());
      console.log('Data selecionada formatada:', format(selectedDate, 'yyyy-MM-dd'));
      
      if (availabilityData?.days) {
        console.log('Dias disponíveis:', availabilityData.days.map(d => d.date));
        const dayAvail = selectedDayAvailability();
        console.log('Dia encontrado:', dayAvail);
        
        if (dayAvail) {
          console.log('Horários disponíveis:', 
            dayAvail.timeSlots
              .filter(slot => slot.available)
              .map(slot => slot.time)
          );
        } else {
          console.log('Nenhum horário disponível para esta data.');
        }
      }
    }
  }, [selectedDate, availabilityData, selectedDayAvailability]);

  // Atualizar o estado local quando os dados da API mudarem
  useEffect(() => {
    if (availability) {
      setAvailabilityData(availability);
      console.log('Dados de disponibilidade atualizados:', availability);
    }
  }, [availability]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (appointmentId) {
        try {
          setLoading(true);
          setError(null);
          console.log('Buscando disponibilidade para o ID:', appointmentId);
          const result = await dispatch(fetchAppointmentAvailability(appointmentId)).unwrap();
          console.log('Disponibilidade recebida da API:', result);
          
          // Armazenar os dados de disponibilidade localmente
          setAvailabilityData(result);
        } catch (error) {
          console.error('Erro ao buscar disponibilidade de agendamento:', error);
          setError('Não foi possível carregar a disponibilidade de horários. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      } else {
        setError('ID de agendamento não fornecido');
      }
    };

    fetchAvailability();
  }, [dispatch, appointmentId]);

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {
      setConfirmationOpen(true);
    }
  };

  // Função para criar a data ISO com o horário selecionado
  const createDateTimeISO = (date: Date, timeString: string): string => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(date);
    
    // Definir as horas e minutos na data
    newDate.setHours(hours, minutes, 0, 0);
    
    return newDate.toISOString();
  };

  const handleConfirmSchedule = async () => {
    // Usar apenas o actualFormId que já foi calculado no useMemo
    // Este valor já prioriza o Redux, depois props, depois processId como último recurso
    if (!selectedDate || !selectedTime || !appointmentId || !actualFormId) {
      console.error('Dados incompletos para agendamento:', {
        selectedDate,
        selectedTime,
        appointmentId,
        actualFormId
      });
      return;
    }
    
    // Verificação adicional para evitar enviar "new" como ID
    if (actualFormId === 'new') {
      setError('Não foi possível identificar o formulário preenchido. Por favor, tente novamente mais tarde.');
      return;
    }

    try {
      setSchedulingInProgress(true);
      setError(null);
      
      // Criar o payload para a API
      const dateTimeISO = createDateTimeISO(selectedDate, selectedTime);
      
      const payload = {
        appointmentId,
        date: dateTimeISO,
        filledFormId: actualFormId
      };
      
      console.log('Enviando agendamento:', payload);
      
      // Chamar a API para criar o agendamento
      const response = await appointmentService.createSchedule(payload);
      
      console.log('Agendamento criado com sucesso:', response);
      setScheduleResponse(response);
      
      setConfirmationOpen(false);
      setScheduleComplete(true);
    } catch (error: any) {
      console.error('Erro ao agendar reunião:', error);
      
      // Extrair a mensagem de erro
      const errorMessage = error.message || 
                         (error.response?.data?.message) || 
                         'Não foi possível agendar a reunião. Por favor, tente novamente.';
      
      setError(errorMessage);
      setConfirmationOpen(false);
    } finally {
      setSchedulingInProgress(false);
    }
  };

  const handleCloseCompleted = () => {
    // Opcional: Adicionar lógica adicional ao fechar
    setScheduleComplete(false);
  };

  // Function to determine which dates are available
  const getAvailableDates = useCallback(() => {
    if (!availabilityData?.days) return [];
    
    return availabilityData.days
      .filter(day => day.available)
      .map(day => {
        try {
          const [year, month, dayOfMonth] = day.date.split('-').map(Number);
          // Criar a data sem considerar o fuso horário
          return new Date(year, month - 1, dayOfMonth, 12, 0, 0);
        } catch (error) {
          console.error('Erro ao converter data:', day.date, error);
          return null;
        }
      })
      .filter((date): date is Date => date !== null);
  }, [availabilityData]);

  const availableTimeSlots = getAvailableTimeSlots();
  const isButtonDisabled = !selectedDate || !selectedTime || schedulingInProgress;

  if (loading || isLoading) {
    return (
      <div className="w-full max-w-[1049px] mx-auto bg-white/10 backdrop-blur-sm rounded-[16px] shadow-lg p-8 text-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
          <p className="text-white text-lg">Carregando disponibilidade de horários...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-[1049px] mx-auto bg-white/10 backdrop-blur-sm rounded-[16px] shadow-lg p-8 text-center">
        <p className="text-white text-lg mb-4">{error}</p>
        <Button 
          onClick={() => window.location.reload()}
          className="mt-4"
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1049px] mx-auto bg-white/10 backdrop-blur-sm rounded-[16px] shadow-lg overflow-hidden flex flex-col">
      {/* Área rolável */}
      <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
        {!scheduleComplete ? (
          <>
            {/* Mensagem de agradecimento */}
            <div className="p-4 sm:p-6 md:p-[40px] border-b border-white/10">
              <h1 className="w-full text-xl sm:text-2xl md:text-[28px] font-semibold text-white mb-2 sm:mb-[12px] text-center">
                {formTitle}
              </h1>
              <p className="w-full text-sm sm:text-base md:text-[16px] text-white/80 leading-normal sm:leading-[24px] text-center">
                Gostaríamos de agendar uma reunião para discutir os próximos passos. Por favor, selecione uma data e horário convenientes para você.
              </p>
            </div>

            {/* Área de agendamento */}
            <div className="p-4 sm:p-6 md:pt-[40px] md:pb-[30px] md:px-[40px] lg:px-[100px]">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-[40px]">
                {/* Coluna do calendário */}
                <div className="flex-1">
                  <h2 className="text-base sm:text-lg md:text-[18px] font-semibold text-white mb-4 sm:mb-[24px]">
                    Selecione uma data
                  </h2>
                  <Calendar 
                    selectedDate={selectedDate} 
                    setSelectedDate={handleDateChange} 
                    availableDates={getAvailableDates()}
                    minimumAdvanceDays={availabilityData?.minimumAdvanceDays}
                    maximumFutureDays={availabilityData?.maximumFutureDays}
                  />
                </div>

                {/* Coluna dos horários */}
                <div className="flex-1 mt-6 lg:mt-0">
                  <h2 className="text-base sm:text-lg md:text-[18px] font-semibold text-white mb-2 sm:mb-[12px]">
                    Selecione um horário
                  </h2>
                  <p className="text-xs sm:text-sm md:text-[14px] text-white/80 mb-4 sm:mb-[24px]">
                    Nossa equipe está disponível nos seguintes horários para atendê-lo. Escolha o que melhor se adequa à sua agenda.
                  </p>
                  <TimeSlots 
                    selectedTime={selectedTime} 
                    setSelectedTime={setSelectedTime}
                    availableTimeSlots={availableTimeSlots}
                  />
                  {selectedDate && availableTimeSlots.length === 0 && (
                    <p className="text-white/70 text-sm mt-4">Nenhum horário disponível para esta data. Por favor, selecione outra data.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Botão de confirmação fixo */}
      {!scheduleComplete && (
        <div className="sticky bottom-0 bg-black/20 border-t border-white/10 p-4 sm:p-6 md:p-[40px] mt-auto backdrop-blur-sm">
          <div className="flex justify-end">
            <Button 
              onClick={handleSchedule}
              disabled={isButtonDisabled}
              className={`h-10 sm:h-[48px] px-4 sm:px-[24px] text-sm sm:text-[16px] font-medium ${
                isButtonDisabled 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-[#1465B3]'
              }`}
            >
              {schedulingInProgress ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Agendar Reunião'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Modal de confirmação */}
      <ScheduleConfirmationDialog
        open={confirmationOpen}
        onOpenChange={setConfirmationOpen}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onConfirm={handleConfirmSchedule}
      />

      {/* Status de agendamento concluído */}
      <ScheduleCompletedStatus
        open={scheduleComplete}
        onOpenChange={setScheduleComplete}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onClose={handleCloseCompleted}
        scheduleResponse={scheduleResponse}
      />
    </div>
  );
}; 